import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Autocomplete, Box, Button, CircularProgress, Divider, Grid, ListItem, ListItemAvatar, ListItemText, Popper, Radio, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { green, grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory } from 'react-router';

import { DataAPIContext } from '../api';
import { CREATE, DELETE, GET_ONE, UPDATE } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import DeleteButton from '../components/DeleteButton';
import downloadBlobResponse from '../components/downloadBlobResponse';
import FixedLinearProgress from '../components/FixedLinearProgress';
import OutlinedExportButton from '../components/OutlinedExportButton';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import AddNewNotification from './AddNewNotification';
import { ActionsColumn, BookingStateDataGridCol, DeliveryByDataGridCol, RolesDataGridCol, TourOverrideModeDataGridCol, ToursDataGridCol } from './CustomDataGridCols';
import NotificationListToolbar from './NotificationListToolbar';
import { ConfirmationDialogText, EVENTS, TOUR_OVERRIDE_MODE_NONOVERRIDABLE, TOUR_OVERRIDE_MODE_OVERRIDE } from './utils';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        paddingBottom: '1rem !important',
    },
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 15rem) !important`,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        // Rows size to their content (autoRowHeight) but never below the usual 125px; the
        // grid sets an inline min-height of 'auto' on these cells, hence !important. It
        // also drops their overflow clipping, which kept content out of the next column.
        '&.MuiDataGrid-root .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
            minHeight: '125px !important',
            overflow: 'hidden',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
    },
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    dm_disabled: {
        textDecoration: 'line-through',
    },
}));

const formatRecordFiltersToMatchAPI = (filters) => {
    let new_filters = [];
    let filter_keys = Object.keys(filters);
    for (let index = 0; index < filter_keys.length; index++) {
        let key = filter_keys[index];
        if (key === 'delivery_method' && filters[key] != null) {
            const raw = filters[key].id;
            if (raw !== undefined && raw !== null && raw !== '') {
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n)) {
                    new_filters = [...new_filters, { field: key, value: [n] }];
                }
            }
            continue;
        }
        if (key === 'role') {
            const roles = filters[key];
            if (Array.isArray(roles) && roles.length > 0) {
                new_filters = [...new_filters, { field: key, value: roles.map((f) => f.id) }];
            }
            continue;
        }
        if (key === 'event' && filters[key] != null) {
            const rawId = filters[key].id ?? filters[key].value;
            if (rawId != null && rawId !== '') {
                new_filters = [...new_filters, { field: key, value: String(rawId) }];
            }
            continue;
        }
        if (key === 'tour' && filters[key]) {
            new_filters = [...new_filters, { field: key, value: filters[key].id }];
            continue;
        }
        new_filters = [...new_filters, { field: key, value: filters[key] }];
    }

    return new_filters;
};

// Delivered by shows every method, so a notification with many of them needs a taller row.
const autoRowHeight = () => 'auto';

const NOTIFICATION_TAB_FILTERS = [
    { title: 'DEFAULT (ALL TOURS)', name: 'default', value: [TOUR_OVERRIDE_MODE_NONOVERRIDABLE, 1] },
    { title: 'TOUR SPECIFIC', name: 'tour_specific', value: TOUR_OVERRIDE_MODE_OVERRIDE },
];

function getCorrectTabFilterBasedOnName() {
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab === 'tour_specific') {
        return NOTIFICATION_TAB_FILTERS.find((t) => t.name === 'tour_specific') || NOTIFICATION_TAB_FILTERS[0];
    }
    return NOTIFICATION_TAB_FILTERS[0];
}


const NotificationList = (props) => {
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'notification';
    const classes = useStyles();
    const [active, setActive] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState();
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const { roleMap, hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    // const allowedTabFilters = React.useMemo(
    //     () => buildAllowedTabFilters(userCanAccessCampaignsUi()),
    //     [],
    // );
    const [tabFilter, setTabFilter] = React.useState(() => {
        const fromUrl = getCorrectTabFilterBasedOnName();
        const last = props.lastTabSelected;
        const lastAllowed =
            last && NOTIFICATION_TAB_FILTERS.some((t) => t.name === last.name) ? last : null;
        return fromUrl || lastAllowed || NOTIFICATION_TAB_FILTERS[0];
    });

    const [relatedLoading, setRelatedLoading] = React.useState(false);
    const [addNotificationAnchorEl, setAddNotificationAnchorEl] = React.useState(null);
    const [duplicateSettings, setDuplicateSettings] = React.useState({});
    const [recordListPageReset, setRecordListPageReset] = React.useState(new Date().toString());
    const createBt = React.useRef(null);
    const btSize = isSmallScreen ? 'medium' : 'small';
    const [exporting, setExporting] = React.useState(false);
    const { recordListFilters, setRecordListFilters } = props;
    // Columns are drag-resizable (CP-8474). Event and Delivered by share the free width;
    // the rest are sized to their content, so they rarely need a drag.
    const defaultRecordListCols = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'tours',
            headerName: 'Tour(s)',
            sortable: false,
            flex: 0.85,
            minWidth: 140,
            renderCell: (p) => ToursDataGridCol(p),
        },
        {
            field: 'name',
            headerName: 'Event',
            sortable: true,
            flex: 1,
            minWidth: 180,
            renderCell: (p) => {
                return (
                    <div
                        onClick={(e) => {
                            e.preventDefault();
                            history.push('/notifications/' + p.row.id);
                        }}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            minWidth: 0,
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Tooltip title={p.row.name}>
                            <div style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden' }}>
                                {/* Up to two lines before the ellipsis (CP-8474) */}
                                <Typography
                                    style={{ fontSize: 16, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                                    fontWeight={500}
                                >
                                    {p.row.name}
                                </Typography>
                            </div>
                        </Tooltip>

                        <div style={{ color: '#8C8C8C', minWidth: 0, overflow: 'hidden' }}>
                            <Typography style={{ fontSize: 14 }} fontWeight={400} color={grey[400]} noWrap>
                                {EVENTS[p.row.event]}
                            </Typography>
                        </div>
                    </div>
                );
            },
        },
        {
            field: 'roles',
            headerName: 'Triggered by',
            sortable: false,
            width: 120,
            renderCell: (p) => RolesDataGridCol(p, roleMap),
        },
        {
            field: 'delivered_by',
            headerName: 'Delivered by',
            sortable: false,
            flex: 1,
            minWidth: 220,
            renderCell: (p) => DeliveryByDataGridCol(p),
        },
        {
            field: 'updated',
            headerName: 'Updated',
            sortable: false,
            width: 110,
            renderCell: (p) => {
                return (
                    <div>
                        <Typography>{moment(p.row.updated_ts).fromNow()}</Typography>
                    </div>
                );
            },
        },
        {
            field: 'booking_state',
            headerName: 'Booking State',
            sortable: false,
            width: 130,
            renderCell: (p) => BookingStateDataGridCol(p),
        },
        {
            field: 'tour_override_mode',
            headerName: 'Tour Override Mode',
            sortable: false,
            width: 150,
            renderCell: (p) => TourOverrideModeDataGridCol(p),
        },
        {
            field: 'buttons',
            headerName: 'Action',
            sortable: false,
            width: 160,
            renderCell: (p) => ActionsColumn(p, handleDuplicate, onDeleteNotification, onSwitchChange, history),
        },
    ];

    const recordListListColumns_default = defaultRecordListCols.filter((col) => col.field !== 'tours');
    const recordListListColumns_tourSpecific = defaultRecordListCols.filter((col) => col.field !== 'roles');
    const recordListListColumns = tabFilter.value === TOUR_OVERRIDE_MODE_OVERRIDE ? recordListListColumns_tourSpecific : recordListListColumns_default;
    const formattedRecordListFilters = React.useMemo(() => {
        return [...formatRecordFiltersToMatchAPI(recordListFilters), { field: 'tour_override_mode', value: tabFilter.value }];
    }, [recordListFilters, tabFilter]);

    const listItemRenderer = (row, idx, rows, gridApi) => {
        return (
            <React.Fragment key={idx}>
                <ListItem alignItems="center" className={classes.listItem}>
                    <ListItemAvatar onClick={(ev) => hasPerm('notification', 'read') ? history.push(`/notifications/${row.id}`) : null}>{row.enabled ? <Tooltip title="Enabled"><CheckCircleIcon color="primary" fontSize="large" /></Tooltip> : <Tooltip title="Disabled"><CancelIcon fontSize="large" color="secondary" /></Tooltip>}</ListItemAvatar>
                    <ListItemText primary={row.name} secondary={row.description} onClick={(ev) => hasPerm('notification', 'read') ? history.push(`/notifications/${row.id}`) : null} />
                    <Switch
                        checked={row?.enabled}
                        disabled={!hasPerm('notification', 'write')}
                        onChange={(e) => {
                            e.preventDefault();
                            onSwitchChange(row);
                        }}
                        name="enabled"
                        color="primary"
                    />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    React.useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('creatingNotification');
        history.push({ search: searchParams.toString() });
        setAddNotificationAnchorEl(null);

    },[]);

    
    const onSelectionModelChange = (ids, selection) => {
        setSelectedIds(selection);
    };

    // The row's delete: this one notification, enabled or not.
    const doDeleteNotification = (id) => {
        setSyncActive(true);
        dataAPI(DELETE, resource, { id })
            .then((res) => {
                if (res?.statusCode === 204) {
                    enqueueSnackbar('Notification deleted', { variant: 'default' });
                    setLastUpdate(new Date().toString());
                    setIdsToDelete(null);
                } else {
                    enqueueSnackbar(res?.data?.message || 'Error deleting notification', { variant: 'error' });
                }
            })
            .catch((err) => {
                enqueueSnackbar('Error deleting notification', { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(false);
            });
    };

    // CP-7771: the selection's delete runs as a background task, even for one row. It keeps enabled notifications
    // and templates of campaigns not sent yet (the backend says which), and the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['notification', 'notifications'], onDeleted: refreshList });
    const doDeleteSelectedNotifications = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setIdsToDelete(null);
                    setSelectedIds([]);
                }
            })
            .finally(() => setSyncActive(false));
    };

    const onDeleteNotification = (notification) => {
        setIdsToDelete([notification.id]);
        setActive(notification);
        setConfirmationDialogAction('delete_notification');
    };

    const onDeleteSelectedNotifications = () => {
        setIdsToDelete(selectedIds);
        setConfirmationDialogAction('delete_notifications');
    };

    const confirmationDialogCancel = () => {
        setDuplicateSettings({});
        setConfirmationDialogAction(null);
    };

    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_notification':
                doDeleteNotification(idsToDelete[0]);
                break;
            case 'delete_notifications':
                doDeleteSelectedNotifications(idsToDelete);
                break;
            case 'duplicate_notification':
                doDuplicate(active);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const onSwitchChange = async (notification) => {
        const params = {
            id: notification.id,
            data: {
                enabled: !notification.enabled,
            },
        };
        setRelatedLoading(false);

        await dataAPI(UPDATE, resource, params)
            .then((res) => {
                if (res.statusCode === 200 || res.statusCode === 201) {
                    enqueueSnackbar('Notification updated', { variant: 'success' });
                    setLastUpdate(new Date().toString());
                } else if (res.statusCode === 406 || res.statusCode === 400) {
                    if (res.data && res.data.message) {
                        enqueueSnackbar(res.data.message, { variant: 'error', autoHideDuration: 5000 });
                    }
                }
            })
            .catch((err, ...args) => console.log('update error', err, args))
            .finally(() => {
                setSyncActive(false);
                setRelatedLoading(false);
            });
    };

    const handleDuplicate = (notification) => {
        setActive(notification);
        setDuplicateSettings((prev) => ({ ...prev, tours: notification.tours }));
        setConfirmationDialogAction('duplicate_notification');
    };

    const doDuplicate = () => {
        setSyncActive(true);
        (async () => {
            try {
                let formatted_tours = [];
                if (duplicateSettings?.tours) {
                    formatted_tours = [...duplicateSettings.tours];
                }

                formatted_tours = formatted_tours?.map((tour) => tour.id);
                const res = await dataAPI(CREATE, resource, {
                    data: {
                        tour_override_mode: duplicateSettings.tour_override_mode,
                        tours: formatted_tours,
                        enabled:false
                    },
                    urlSuffix: active.id + '/duplicate/',
                });
                enqueueSnackbar('Notification duplicated', {
                    variant: 'success',
                    action: (key) => (
                        <React.Fragment>
                            <Button
                                onClick={() => {
                                    closeSnackbar(key);
                                    history.push(`/notifications/${res.data.id}`);
                                }}
                            >
                                Open it
                            </Button>
                            <Button
                                onClick={() => {
                                    closeSnackbar(key);
                                }}
                            >
                                Dismiss
                            </Button>
                        </React.Fragment>
                    ),
                });
                setLastUpdate(new Date().toString());
            } catch (e) {
                enqueueSnackbar('Error duplicating notification', { variant: 'error' });
                console.error('Error duplicating notification', e);
            } finally {
                setSyncActive(false);
                setDuplicateSettings({});
            }
        })();
    };

    const handleNotificationDuplicateTypeChange = (ev, nt) => {
        if (!nt) {
            return;
        }
        setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: nt.value }));
    };
    const handleNotificationDuplicateToursChange = (ev, tours) => {
        setDuplicateSettings((prev) => ({ ...prev, tours: tours }));
    };

    const handleTabFilterSwitch = async (atf) => {
        const next = new URLSearchParams();
        next.set('tab', atf.name);
        history.push({ search: next.toString() });
        if (atf.name === 'default') {
            setRecordListFilters((prev) => ({ ...prev, tour: null }));
        }
        setTabFilter(atf);
        props.setLastTabSelected(atf);
        await setRecordListPageReset(new Date().toString());
        await setLastUpdate(new Date().toString());
    };

    React.useEffect(() => {
        // Using history.push does not clean the recordList cache so updates don't show up in the list without this timeout
        setTimeout(() => setLastUpdate(new Date().toString()), 300);
    }, []);

    const handleOnAddNotificationClose = () => {
        history.push({ search: '' });
        setAddNotificationAnchorEl(null);
    };

    // Downloads the operator's COMPLETE notification set as a ZIP archive
    // (server-side dump; list filters and the tab are intentionally not applied).
    const handleExportZip = async () => {
        try {
            setExporting(true);
            const res = await dataAPI(GET_ONE, resource, { urlSuffix: 'export/' });
            await downloadBlobResponse(res, `notifications-${new Date().toISOString().slice(0, 10)}.zip`);
        } catch (error) {
            console.error('handleExportZip: error exporting notifications:', error);
            enqueueSnackbar('Failed to export notifications', { variant: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const handleCreateNotification = (element) => {
        history.push({ search: '?creatingNotification=true' });
        setAddNotificationAnchorEl(element)
    }

    return (
        <div className={classes.root}>
            <FixedLinearProgress visibility={syncActive} />
            <PageMeta title="Notifications" />
            {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {confirmationDialogAction !== 'delete_notification' &&
                        confirmationDialogAction !== 'delete_notifications' &&
                        confirmationDialogAction !== 'duplicate_notification' &&
                        ConfirmationDialogText[confirmationDialogAction].body}
                    {confirmationDialogAction === 'delete_notifications' && (
                        <Typography>
                            Delete the {idsToDelete?.length} selected notification{idsToDelete?.length === 1 ? '' : 's'}? Enabled
                            notifications, and templates of campaigns not sent yet, are kept: disable them first. Deleted
                            notifications cannot be restored.
                        </Typography>
                    )}
                    {confirmationDialogAction === 'delete_notification' && (
                        <Typography>
                            {ConfirmationDialogText[confirmationDialogAction].body}
                            <span style={{ fontWeight: 700 }}>{active?.name || 'Notification'}</span> This action is irreversible. Are you sure?
                        </Typography>
                    )}
                    {confirmationDialogAction === 'duplicate_notification' && ConfirmationDialogText[confirmationDialogAction].body + (active?.name || 'Notification') + '?'}
                    {confirmationDialogAction === 'duplicate_notification' && (
                        <>
                            <Grid item xs={12} mt={2}>
                                <Typography>Choose the type of notification you want to create:</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={[
                                        { name: 'non_override', id: 0, label: 'Default', value: 0 },
                                        { name: 'override', id: 2, label: 'Tour Specific', value: 2 },
                                    ]}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={handleNotificationDuplicateTypeChange}
                                    renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Type" />}
                                />
                            </Grid>
                            {(duplicateSettings.tour_override_mode === 0 || duplicateSettings.tour_override_mode === 1) && (
                                <Grid item xs={12} container>
                                    <Grid item container alignItems="center">
                                        <Radio value={0} name="non_override" checked={duplicateSettings.tour_override_mode === 0} onClick={() => setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: 0 }))} />
                                        <Typography>Non overridable - Will be sent to all tours</Typography>
                                    </Grid>
                                    <Grid item container alignItems="center" justifyContent="flex-start">
                                        <Radio value={1} name="overridable" checked={duplicateSettings.tour_override_mode === 1} onClick={() => setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: 1 }))} />
                                        <Typography>Overridable - Will be sent to all tours except</Typography>
                                        <Typography>the ones that have the same tour-specific notification</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {duplicateSettings.tour_override_mode === 2 && (
                                <>
                                    <Grid item xs={12} mt={2}>
                                        <Typography>Please choose the tour(s) for the variant:</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RemoteAutocomplete
                                            filterSelected
                                            filterOptions={(options) => options}
                                            required
                                            InputProps={{ required: true }}
                                            resource="tour"
                                            label="Select Tour:"
                                            multiple
                                            variant="filled"
                                            size="small"
                                            onChange={handleNotificationDuplicateToursChange}
                                            value={duplicateSettings.tours}
                                            getOptionLabel={(opt) => {
                                                if (opt.is_bespoke) {
                                                    return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                                                }
                                                return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                                            }}
                                            renderOption={(props, option) => {
                                                if (option.is_bespoke) {
                                                    return (
                                                        <div {...props}>
                                                            [id:{option.id}] {option.name} ({option.start_date} - {option.end_date})
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div {...props}>
                                                        [id:{option.id}] {option.name} (Recurring)
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </ConfirmationDialog>
            ) : null}

            <Grid>
                <Popper
                    open={new URLSearchParams(window?.location?.search).get('creatingNotification') === 'true'}
                    style={{ zIndex: 1, marginTop: '7rem' }}
                    anchorEl={addNotificationAnchorEl}
                    onClose={() => setAddNotificationAnchorEl(null)}
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={[
                        {
                            name: 'flip',
                            enabled: true,
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                boundary: 'scrollParent',
                            },
                        },
                    ]}
                >
                    <AddNewNotification setLastUpdate={setLastUpdate} setSyncActive={setSyncActive} onClose={handleOnAddNotificationClose} syncActive={syncActive} isSmallScreen={isSmallScreen} />
                </Popper>
            </Grid>
            <Grid item xs={12} pt={2} pl={2} pr={2} container justifyContent="space-between" alignItems="center">
                <Typography fontSize={20} fontWeight={500}>Notifications</Typography>
                {/* [+] and Export beside it, like Trips (CP-8396); icon-only on
                    a phone. The column sizes to its buttons: a fixed Grid
                    width wrapped "New notification" onto two lines. */}
                <Box display="flex" alignItems="center">
                    {selectedIds?.length > 0 && !isSmallScreen && (
                        <DeleteButton perm="notification.delete" size={btSize} sx={{ mr: 1, whiteSpace: 'nowrap' }} onClick={onDeleteSelectedNotifications}>
                            Delete {selectedIds.length === 1 ? 'notification' : `${selectedIds.length} notifications`}
                        </DeleteButton>
                    )}
                    {hasPerm('notification', 'write') && (
                        <Tooltip title={isSmallScreen ? 'New notification' : ''}>
                            <Button ref={createBt} color="primary" variant="contained" size={btSize} sx={{ whiteSpace: 'nowrap' }} onClick={(ev) => handleCreateNotification(ev.currentTarget)}>
                                <AddIcon />
                                {!isSmallScreen && <>&nbsp;New notification</>}
                            </Button>
                        </Tooltip>
                    )}
                    {hasPerm('notification', 'export') && (
                        <OutlinedExportButton
                            onClick={handleExportZip}
                            disabled={exporting}
                            size={btSize}
                            hideLabel={isSmallScreen}
                            tooltip="Export all notifications as ZIP (rules, templates, tags — full dump)"
                            icon={exporting ? <CircularProgress size={14} /> : <FileUploadOutlinedIcon />}
                            sx={{ ml: 1 }}
                        />
                    )}
                </Box>

                {/* {isSmallScreen && (
                    <Box position="fixed" right={theme.spacing(3)} bottom={theme.spacing(3)} zIndex={99000}>
                        <Fab color="primary" onClick={(ev) => setAddNotificationAnchorEl(ev.currentTarget)}>
                            <AddIcon />
                        </Fab>
                    </Box>
                )} */}
            </Grid>
            <Grid item xs={12} style={{ margin: '1rem', border: 'solid 1px #D8D8D8', borderRadius: '12px' }}>
                <div style={{ display: 'flex', height: '48px', paddingLeft: '1rem', gap: '1rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
                    {NOTIFICATION_TAB_FILTERS.map((atf) => {
                        return (
                            <div key={atf.name} onClick={() => handleTabFilterSwitch(atf)} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                <Typography style={{ color: tabFilter.name === atf.name ? '#007AFF' : '#747475', borderBottom: tabFilter.name === atf.name && 'solid 2px #007AFF', lineHeight: '48px', whiteSpace: 'nowrap' }} fontSize={14}>
                                    {atf.title}
                                </Typography>
                            </div>
                        );
                    })}
                </div>

                <Grid item xs={12}>
                    <Divider />
                </Grid>
                <Grid item xs={12} p={isSmallScreen && 2} pb={0}>
                    <NotificationListToolbar
                        resource={resource}
                        handleCreateNotification={handleCreateNotification}
                        setQuery={props.setRecordListQuery}
                        query={props.recordListQuery}
                        selectedIds={selectedIds}
                        isSmallScreen={isSmallScreen}
                        roleMap={roleMap}
                        tabFilter={tabFilter}
                        recordListFilters={recordListFilters}
                        setRecordListFilters={setRecordListFilters}
                        setLastUpdate={setLastUpdate}
                        setRecordListPageReset={setRecordListPageReset}
                    />
                </Grid>
                <Grid item xs={12} className={classes.recordListWrapper}>
                    <RecordList
                        className={classes.recordList}
                        resource={resource}
                        flavor="pro"
                        persistColumnWidths={true}
                        // Resizing only: a dragged column order would snap back on the next render.
                        disableColumnReorder={true}
                        getRowHeight={autoRowHeight}
                        disableSelectionOnClick={true}
                        selectionModel={selectedIds}
                        onSelectionModelChange={onSelectionModelChange}
                        apiFilters={formattedRecordListFilters}
                        extraQueryParams={{ view: 'list' }}
                        query={props.recordListQuery}
                        sortModel={[{ field: 'name', sort: 'asc' }]}
                        deps={lastUpdate}
                        disableColumnFilter={true}
                        listItemRenderer={listItemRenderer}
                        disableListBorder={true}
                        checkboxSelection={hasPerm('notification', 'delete')}
                        disablePageUrlData={true}
                        pageReset={recordListPageReset}
                        rowHeight={125}
                        relatedLoading={relatedLoading}
                        columns={[...recordListListColumns]}
                        noMatchesLabel={tabFilter?.name === 'default' ? 'No Notifications' : 'No Tour Specific Notifications'}
                        disableColumnMenu={true}
                    />
                </Grid>
            </Grid>
        </div>
    );
};

export default NotificationList;
